import type {
  DashboardDecorationPlacement,
  DashboardFieldBinding,
  DashboardTableBinding,
  DashboardTableColumnBinding,
  DataQueryExecutionResult,
} from "../types/dashboard";
import {
  parseStaticTable,
  stringifyStaticTable,
  type StaticTableData,
} from "./decorationCatalog";

export function getValueAtPath(root: unknown, path: string): unknown {
  if (!path.trim()) return undefined;
  const parts = path.split(".");
  let current: unknown = root;

  for (const part of parts) {
    if (current === null || current === undefined) return undefined;

    const bracketMatch = part.match(/^([^[]+)\[(\d+)\]$/);
    if (bracketMatch) {
      const [, key, indexText] = bracketMatch;
      if (typeof current !== "object") return undefined;
      const container = (current as Record<string, unknown>)[key];
      if (!Array.isArray(container)) return undefined;
      current = container[Number(indexText)];
      continue;
    }

    if (/^\d+$/.test(part)) {
      if (!Array.isArray(current)) return undefined;
      current = current[Number(part)];
      continue;
    }

    if (typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

export function formatBoundValue(value: unknown, format?: string): string {
  if (value === null || value === undefined) return "";

  switch (format) {
    case "number": {
      const n = Number(value);
      return Number.isFinite(n) ? n.toLocaleString() : String(value);
    }
    case "percent": {
      const n = Number(value);
      return Number.isFinite(n) ? `${(n * 100).toFixed(1)}%` : String(value);
    }
    case "date": {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    }
    default:
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
  }
}

function resolveBindingValue(
  binding: DashboardFieldBinding,
  dataQueries: DataQueryExecutionResult[]
): string | undefined {
  const queryResult = dataQueries.find((q) => q.queryId === binding.queryId);
  if (!queryResult) return binding.fallback;
  if (queryResult.error) return binding.fallback;

  const raw = getValueAtPath(queryResult.openSearchResponse, binding.valuePath);
  if (raw === null || raw === undefined || raw === "") {
    return binding.fallback;
  }

  const formatted = formatBoundValue(raw, binding.format);
  return formatted || binding.fallback;
}

function resolveColumnCellValue(
  item: unknown,
  column: DashboardTableColumnBinding | undefined,
  staticFallback?: string
): string {
  if (!column?.fieldPath?.trim()) {
    return staticFallback ?? "";
  }
  const raw = getValueAtPath(item, column.fieldPath);
  if (raw === null || raw === undefined || raw === "") {
    return column.fallback ?? staticFallback ?? "";
  }
  const formatted = formatBoundValue(raw, column.format);
  return formatted || column.fallback || staticFallback || "";
}

export function resolveTableData(
  decoration: DashboardDecorationPlacement,
  dataQueries: DataQueryExecutionResult[] = []
): StaticTableData {
  const staticTable = parseStaticTable(decoration.content);
  const tableBinding = decoration.tableBinding;
  if (!tableBinding?.queryId || !tableBinding.resultPath?.trim()) {
    return staticTable;
  }

  const queryResult = dataQueries.find((q) => q.queryId === tableBinding.queryId);
  if (!queryResult || queryResult.error) {
    return staticTable;
  }

  const sourceRows = getValueAtPath(queryResult.openSearchResponse, tableBinding.resultPath);
  if (!Array.isArray(sourceRows)) {
    return staticTable;
  }

  const columnBindings = tableBinding.columns ?? [];
  const rows = sourceRows.map((item, rowIndex) =>
    staticTable.headers.map((_, columnIndex) => {
      const staticCell = staticTable.rows[rowIndex]?.[columnIndex];
      return resolveColumnCellValue(item, columnBindings[columnIndex], staticCell);
    })
  );

  return { headers: staticTable.headers, rows };
}

function applyFieldValue(
  target: DashboardDecorationPlacement,
  fieldPath: string,
  value: string
): void {
  if (fieldPath === "title") {
    target.title = value;
    return;
  }
  if (fieldPath === "content") {
    target.content = value;
    return;
  }
  if (fieldPath.startsWith("style.")) {
    const styleKey = fieldPath.slice("style.".length);
    target.style = { ...target.style, [styleKey]: value };
  }
}

export function decorationHasBindings(item: DashboardDecorationPlacement): boolean {
  if (Object.keys(item.bindings ?? {}).length > 0) {
    return true;
  }
  const tableBinding = item.tableBinding;
  return Boolean(
    tableBinding?.queryId &&
      tableBinding.resultPath?.trim() &&
      tableBinding.columns?.some((column) => column.fieldPath?.trim())
  );
}

export function isQueryReferencedInDecorations(
  queryId: string,
  decorations: DashboardDecorationPlacement[]
): boolean {
  return decorations.some((decoration) => {
    if (decoration.tableBinding?.queryId === queryId) {
      return true;
    }
    return Object.values(decoration.bindings ?? {}).some((binding) => binding.queryId === queryId);
  });
}

export function syncTableBindingColumns(
  decoration: DashboardDecorationPlacement
): DashboardTableBinding | undefined {
  const tableBinding = decoration.tableBinding;
  if (!tableBinding) {
    return undefined;
  }
  const headerCount = parseStaticTable(decoration.content).headers.length;
  const existing = tableBinding.columns ?? [];
  const columns: DashboardTableColumnBinding[] = Array.from({ length: headerCount }, (_, index) => ({
    fieldPath: existing[index]?.fieldPath ?? "",
    format: existing[index]?.format,
    fallback: existing[index]?.fallback,
  }));
  return { ...tableBinding, columns };
}

export function resolveDecorationBindings(
  decoration: DashboardDecorationPlacement,
  dataQueries: DataQueryExecutionResult[] = []
): DashboardDecorationPlacement {
  const bindings = decoration.bindings;
  const hasFieldBindings = bindings && Object.keys(bindings).length > 0;
  const hasTableBinding = Boolean(decoration.tableBinding?.queryId && decoration.tableBinding.resultPath);

  if (!hasFieldBindings && !hasTableBinding) {
    return decoration;
  }

  const resolved: DashboardDecorationPlacement = {
    ...decoration,
    style: decoration.style ? { ...decoration.style } : undefined,
  };

  if (hasFieldBindings) {
    for (const [fieldPath, binding] of Object.entries(bindings!)) {
      const value = resolveBindingValue(binding, dataQueries);
      if (value !== undefined) {
        applyFieldValue(resolved, fieldPath, value);
      }
    }
  }

  if (decoration.kind === "table" && hasTableBinding) {
    const tableData = resolveTableData(decoration, dataQueries);
    resolved.content = stringifyStaticTable(tableData);
  }

  return resolved;
}
